import React from 'react';

import { type UUID, isDefined } from '@nfctron/nexus-core';
import { type ExtendedComponent, UiButton, useContextRequired, useFormat } from '@nfctron/nexus-react';

import { ShoppingCartIcon } from 'lucide-react';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';

import { CartStoreExtraProductsSlice, useCartStore, useCartStoreExtraProductsProvider } from '../../stores/cartStore';
import { EventCartPageContext } from '../CartContext';

import { ProductCardBase } from './ProductCardBase';

/**
 * Event/Cart/ProductCardProductGroup component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const ProductCardProductGroup: React.FunctionComponent<ProductCardProductGroupProps> = ({ productGroupId, cartTicketId, ...props }) => {
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, ProductCardProductGroup.displayName);
	const { event } = useContextRequired(EventCartPageContext);
	const format = useFormat();

	/** get cart store providers */
	const { groupGetters, sheetConfig } = useCartStoreExtraProductsProvider();

	/** get ticket type id for the carted ticket */
	const ticketTypeId = useCartStore((state) => state.getCartedTicket({ cartTicketId }).ticket.ticketTypeId);

	/** get product group config */
	const productGroupConfig = useCartStore((state) => state.getExtraProductGroupById({ productGroupId }));

	/** get product group state */
	const productGroupIs = useCartStore((state) => state.getExtraProductGroupStateById({ productGroupId, cartTicketId }));

	/** get product group price range */
	const priceRange = useCartStore((state) => state.getExtraProductGroupPriceRange({ productGroupId, ticketTypeId }));

	return (
		<ProductCardBase
			title={productGroupConfig.name}
			description={productGroupConfig.description}
			gallery={productGroupConfig.gallery}
			type={CartStoreExtraProductsSlice.PRODUCT_CONTROL_TYPE.GROUP}
			price={t('priceRange.text', {
				price: format.price(priceRange.min, event.currency.baseCurrency.currencyId),
			})}
			actions={
				<React.Fragment>
					{/* cart button */}
					<UiButton
						variant="primary"
						size="sm"
						leftSection={<ShoppingCartIcon />}
						loading={isDefined(sheetConfig.variantsInput)}
						disabled={productGroupIs.quantityLimited || productGroupIs.soldOut}
						onClick={() => {
							/** group with product variants */
							sheetConfig.setVariantsInput({
								productGroupId,
								cartTicketId,
							});
						}}
					>
						{t('addToCartButton.text')}
					</UiButton>
				</React.Fragment>
			}
			{...props}
		/>
	);
};

/**
 * Event/Cart/ProductCardProductGroup component display name
 * @type {string}
 */
ProductCardProductGroup.displayName = 'Event/Cart/ProductCardProductGroup';

/**
 * Event/Cart/ProductCardProductGroup component props type
 * @export
 */
export type ProductCardProductGroupProps = ExtendedComponent<
	{
		cartTicketId: UUID;
		productGroupId: UUID;
	},
	typeof ProductCardBase
>;
